import { useEffect, useState } from 'react';
import { PlusIcon, SaveIcon, SendIcon, Trash2Icon } from 'lucide-react';
import type {
  ChangelogItem,
  CreateChangelogItemInput,
  ListChangelogAdminResponse,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';

/**
 * Beheer ⟶ Wat is nieuw (issue #238).
 *
 * Eén formulier voor een nieuw item plus de lijst met wat er al staat.
 * Opslaan als concept en vrijgeven zijn dezelfde knop met een andere stand van
 * één boolean; vrijgeven is de enige actie die de bel laat gaan, en maar één
 * keer per item. Daarom staat er een waarschuwing bij die knop.
 *
 * RBAC zit server-side; /beheer/wat-is-nieuw is bovendien door RoleGuard
 * afgeschermd, dus een niet-beheerder komt hier nooit.
 */

const VANDAAG = (): string => {
  // Wall-clock NL-datum, zoals de backend hem verwacht (YYYY-MM-DD).
  const nu = new Date();
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(nu);
};

const LEEG: CreateChangelogItemInput = {
  title: '',
  body: '',
  date: VANDAAG(),
  gepubliceerd: false,
};

const CONCEPT_TEKST = 'Opgeslagen als concept. Niemand ziet het tot je het vrijgeeft.';

/**
 * Twee standen, twee eerlijke teksten. De melding-belofte staat alleen in de
 * tekst als de server hem ook waarmaakt.
 */
function vrijgegevenTekst(meldingenAan: boolean): string {
  return meldingenAan
    ? 'Vrijgegeven. Iedereen met een actief account heeft er een melding over gekregen.'
    : 'Vrijgegeven. Het item staat nu op Wat is nieuw. Meldingen staan uit, dus er is nog geen bel verstuurd.';
}

function herpublicatieTekst(meldingenAan: boolean): string {
  return meldingenAan
    ? 'Weer zichtbaar gezet. Dit item was al eens vrijgegeven, dus er gaat geen nieuwe melding uit.'
    : 'Weer zichtbaar gezet. Er gaat geen melding uit.';
}

export function BeheerWatIsNieuwPage(): JSX.Element {
  const [items, setItems] = useState<ChangelogItem[]>([]);
  const [concept, setConcept] = useState<CreateChangelogItemInput>(LEEG);
  const [loading, setLoading] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // Serverwaarheid, geen aanname: met de meldingen gedoofd mag dit scherm niet
  // beloven dat iedereen een bel krijgt. Start op false zodat de knop bij een
  // trage lading niet eerst het ruimste verhaal vertelt.
  const [meldingenAan, setMeldingenAan] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function herlaad(): Promise<void> {
    const resp = await apiGet<ListChangelogAdminResponse>('/api/v1/beheer/changelog');
    setItems(resp.items);
    setMeldingenAan(resp.meldingenAan);
  }

  useEffect(() => {
    (async () => {
      try {
        await herlaad();
      } catch (err) {
        setErrorMsg(meldFout(err, 'Laden mislukt'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function bewaar(gepubliceerd: boolean): Promise<void> {
    setBezig(true);
    setErrorMsg(null);
    setToast(null);
    try {
      await apiPost<{ item: ChangelogItem }>('/api/v1/beheer/changelog', {
        ...concept,
        gepubliceerd,
      });
      setConcept({ ...LEEG, date: VANDAAG() });
      await herlaad();
      setToast(gepubliceerd ? vrijgegevenTekst(meldingenAan) : CONCEPT_TEKST);
    } catch (err) {
      setErrorMsg(meldFout(err, 'Opslaan mislukt'));
    } finally {
      setBezig(false);
    }
  }

  async function geefVrij(item: ChangelogItem): Promise<void> {
    setBezig(true);
    setErrorMsg(null);
    setToast(null);
    try {
      await apiPatch<{ item: ChangelogItem }>(`/api/v1/beheer/changelog/${item.id}`, {
        gepubliceerd: true,
      });
      await herlaad();
      setToast(
        item.publishedAt === undefined
          ? vrijgegevenTekst(meldingenAan)
          : herpublicatieTekst(meldingenAan),
      );
    } catch (err) {
      setErrorMsg(meldFout(err, 'Vrijgeven mislukt'));
    } finally {
      setBezig(false);
    }
  }

  async function verberg(item: ChangelogItem): Promise<void> {
    setBezig(true);
    setErrorMsg(null);
    setToast(null);
    try {
      await apiPatch<{ item: ChangelogItem }>(`/api/v1/beheer/changelog/${item.id}`, {
        gepubliceerd: false,
      });
      await herlaad();
      setToast('Terug naar concept. De regel is niet meer zichtbaar voor collega’s.');
    } catch (err) {
      setErrorMsg(meldFout(err, 'Wijzigen mislukt'));
    } finally {
      setBezig(false);
    }
  }

  async function verwijder(item: ChangelogItem): Promise<void> {
    if (!window.confirm(`"${item.title}" definitief verwijderen?`)) return;
    setBezig(true);
    setErrorMsg(null);
    setToast(null);
    try {
      await apiDelete(`/api/v1/beheer/changelog/${item.id}`);
      await herlaad();
      setToast('Verwijderd.');
    } catch (err) {
      setErrorMsg(meldFout(err, 'Verwijderen mislukt'));
    } finally {
      setBezig(false);
    }
  }

  const kanOpslaan = concept.title.trim().length > 0 && concept.body.trim().length > 0 && !bezig;

  return (
    <Layout>
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <header className="mb-4">
          <h1 className="text-xl font-bold text-ink-900">Wat is nieuw</h1>
          <p className="mt-1 text-sm text-ink/70">
            Schrijf per wijziging één regel die een collega begrijpt zonder de code te kennen. Wat
            je vrijgeeft komt op de pagina Wat is nieuw{' '}
            {meldingenAan
              ? 'en stuurt één melding naar de bel van iedereen.'
              : 'en is meteen zichtbaar. Meldingen staan nu uit, dus er gaat nog geen bel naar je collega’s.'}{' '}
            Een concept blijft onzichtbaar tot je het vrijgeeft.
          </p>
        </header>

        {errorMsg && (
          <div className="mb-4 rounded-2xl border border-error/30 bg-error/5 px-4 py-3 text-sm text-error">
            {errorMsg}
          </div>
        )}
        {toast && (
          <div className="mb-4 rounded-2xl border border-brand-100 bg-brand-50 px-4 py-3 text-sm text-brand-800">
            {toast}
          </div>
        )}

        <section className="mb-6 rounded-2xl border border-ink-100 bg-white p-5 shadow-card">
          <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-ink-900">
            <PlusIcon className="h-4 w-4 text-brand-700" aria-hidden />
            Nieuw item
          </h2>
          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-semibold text-ink-900">Titel</span>
            <input
              type="text"
              value={concept.title}
              maxLength={160}
              onChange={(e) => setConcept({ ...concept, title: e.target.value })}
              placeholder='Kolom "laatst actief" in Gebruikersbeheer'
              className="w-full rounded-xl border border-ink-100 px-3 py-2 text-sm text-ink focus:border-brand-700 focus:outline-none"
            />
          </label>
          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-semibold text-ink-900">Korte tekst</span>
            <textarea
              value={concept.body}
              maxLength={1000}
              rows={4}
              onChange={(e) => setConcept({ ...concept, body: e.target.value })}
              placeholder="Wat kun je nu dat eerst niet kon? Noem waar het staat."
              className="w-full rounded-xl border border-ink-100 px-3 py-2 text-sm text-ink focus:border-brand-700 focus:outline-none"
            />
            <span className="mt-1 block text-xs text-ink/55">
              {concept.body.length}/1000 tekens
            </span>
          </label>
          <label className="mb-4 block">
            <span className="mb-1 block text-sm font-semibold text-ink-900">Datum</span>
            <input
              type="date"
              value={concept.date}
              onChange={(e) => setConcept({ ...concept, date: e.target.value })}
              className="rounded-xl border border-ink-100 px-3 py-2 text-sm text-ink focus:border-brand-700 focus:outline-none"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!kanOpslaan}
              onClick={() => void bewaar(false)}
              className="inline-flex items-center gap-2 rounded-full border border-ink-100 px-4 py-2 text-sm font-semibold text-ink hover:border-brand-700 disabled:opacity-40"
            >
              <SaveIcon className="h-4 w-4" aria-hidden />
              Bewaar als concept
            </button>
            <button
              type="button"
              disabled={!kanOpslaan}
              onClick={() => void bewaar(true)}
              className="inline-flex items-center gap-2 rounded-full bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-40"
            >
              <SendIcon className="h-4 w-4" aria-hidden />
              {meldingenAan ? 'Vrijgeven en melden' : 'Vrijgeven'}
            </button>
          </div>
        </section>

        <h2 className="mb-2 text-base font-bold text-ink-900">Bestaande items</h2>
        {loading ? (
          <div className="rounded-2xl border border-ink-100 bg-white px-6 py-10 text-center text-sm text-ink/55">
            Laden…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-ink-100 bg-white px-6 py-10 text-center text-sm text-ink/55">
            Nog niets geschreven.
          </div>
        ) : (
          <ol className="flex flex-col gap-3">
            {items.map((item) => (
              <li
                key={item.id}
                className="rounded-2xl border border-ink-100 bg-white px-5 py-4 shadow-card"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-brand-700">
                    {item.date}
                  </span>
                  <span
                    className={
                      item.gepubliceerd
                        ? 'rounded-full border border-ring2-border bg-ring2-bg px-2 py-0.5 text-xs font-semibold text-ring2-fg'
                        : 'rounded-full border border-ring1-border bg-ring1-bg px-2 py-0.5 text-xs font-semibold text-ring1-fg'
                    }
                  >
                    {item.gepubliceerd ? 'Vrijgegeven' : 'Concept'}
                  </span>
                </div>
                <h3 className="mt-1 text-sm font-bold text-ink-900">{item.title}</h3>
                <p className="mt-1 whitespace-pre-line text-sm text-ink">{item.body}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.gepubliceerd ? (
                    <button
                      type="button"
                      disabled={bezig}
                      onClick={() => void verberg(item)}
                      className="rounded-full border border-ink-100 px-3 py-1.5 text-xs font-semibold text-ink hover:border-brand-700 disabled:opacity-40"
                    >
                      Terug naar concept
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={bezig}
                      onClick={() => void geefVrij(item)}
                      className="rounded-full bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-40"
                    >
                      Vrijgeven
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={bezig}
                    onClick={() => void verwijder(item)}
                    className="inline-flex items-center gap-1 rounded-full border border-ink-100 px-3 py-1.5 text-xs font-semibold text-error hover:border-error disabled:opacity-40"
                  >
                    <Trash2Icon className="h-3.5 w-3.5" aria-hidden />
                    Verwijderen
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Layout>
  );
}

function meldFout(err: unknown, prefix: string): string {
  return err instanceof ApiError
    ? `${prefix} (${err.status}): ${err.message}`
    : `${prefix} — probeer het later opnieuw.`;
}
